import React from 'react';
import Image from 'next/image';
import type { SitePhotoData } from '@/data/sitePhotos';
import PhotoRow, { pickRow } from '@/components/ui/PhotoRow';

interface SitePhotoProps {
  photo: SitePhotoData;
  /** Related photos placed beside (or under) the main one when its shape doesn't suit the slot */
  companions?: SitePhotoData[];
  /** Intended slot shape ("4/3", "3/4", …) */
  aspectRatio?: string;
  className?: string;
  priority?: boolean;
  sizes?: string;
  /** The parent decides the size (e.g. a hero column). The photos then fill it edge to edge. */
  fillParent?: boolean;
}

/**
 * Real photo in a slot — never a blurred fill, never a heavy crop:
 *  • photo shape within 12% of the slot → fills the slot;
 *  • otherwise, with companions → photos side by side (or stacked, for tall slots) whose
 *    combined shape best matches the slot;
 *  • otherwise → the slot takes the photo's own shape.
 */
export default function SitePhoto({
  photo,
  companions = [],
  aspectRatio = '4/3',
  className = '',
  priority = false,
  sizes = '(min-width: 1024px) 50vw, 100vw',
  fillParent = false,
}: SitePhotoProps) {
  const [w, h] = aspectRatio.split('/').map(Number);
  const slot = w / h;
  const ratio = photo.width / photo.height;
  const fits = Math.abs(ratio - slot) / slot <= 0.12;
  const all = [photo, ...companions].map((p) => ({ ...p, ratio: p.width / p.height }));

  // Slot sized by its parent: fill it with a row (wide slot) or a stack (tall slot)
  if (fillParent) {
    const tall = slot < ratio * 0.85;
    const chosen = tall
      ? pickRow(all.map((p) => ({ ...p, ratio: 1 / p.ratio })), 1 / slot).map((p) => all.find((q) => q.src === p.src)!)
      : pickRow(all, slot);
    return (
      <div className={`flex ${tall ? 'flex-col' : 'flex-row'} gap-2 overflow-hidden ${className}`}>
        {chosen.map((p, i) => (
          <div
            key={p.src}
            className="relative overflow-hidden min-h-0 min-w-0"
            // ×100: flex-grow values summing below 1 only hand out that fraction of the space
            style={{ flex: `${(tall ? 1 / p.ratio : p.ratio) * 100} 1 0%` }}
          >
            <Image src={p.src} alt={p.alt} fill priority={priority && i === 0} sizes={sizes} className="object-cover" />
          </div>
        ))}
      </div>
    );
  }

  if (fits) {
    return (
      <div
        className={`relative overflow-hidden w-full ${className}`}
        style={{ aspectRatio: aspectRatio.replace('/', ' / '), backgroundColor: '#E8E3DC' }}
      >
        <Image src={photo.src} alt={photo.alt} fill priority={priority} sizes={sizes} className="object-cover" />
      </div>
    );
  }

  // Mismatch: companions side by side (or just the photo at its own shape)
  const row = companions.length ? pickRow(all, slot) : [all[0]];
  return <PhotoRow items={row} priority={priority} sizes={sizes} className={className.replace(/\b(h-full|absolute|inset-0)\b/g, '')} />;
}
